import React, { useEffect, useState, useRef, useMemo, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { api } from "../../api/client";
import {
  Globe, Search, Radar, X, BookmarkPlus, History, Info, CheckCircle,
  AlertTriangle, AlertCircle, Building2, Lock, FileText, ChevronDown,
  Download, RotateCcw, ExternalLink, RefreshCw, Layers, ShieldAlert,
  CheckCircle2, Clock, Filter, ArrowUpDown, Eye, EyeOff, MoreVertical,
  Check, Play, ArrowRight, Share2, Activity, Users, FileImage, Star, Gem, Plus
} from "lucide-react";
import { useAuth } from "../../auth/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";
import { isFreePlanUser } from "../../utils/subscription";
import { AddDomainModal } from "../../components/AddDomainModal";
import {
  searchDomain, getDomainHistory, addToWatchlist, getWatchlist, removeFromWatchlist,
  confirmCompanyProfile, fetchSectorFacts, fetchAdminIntegrationStatus, fetchCompanyProfile,
  fetchDomainCount, searchDomainPhase
} from "../../api/domainMonitor";
import type { CompanyProfile, SectorFacts, AdminIntegrationStatus } from "../../api/domainMonitor";
import {
  createReportJob, fetchActiveReportJob, cancelReportJob, downloadReportPdf, fetchDomainReports
} from "../../api/reports";
import type { ReportJob, ExistingReport } from "../../api/reports";
import { ReportModal } from "../../components/ReportModal";
import { ReportStatusCard } from "../../components/ReportStatusCard";
import { DonutChart } from "../../components/DonutChart";
import type { DomainSearchResult, WatchlistItem, DomainHistoryItem, DomainLeakResult } from "../../types/domainMonitor";
import ForumTypeBadge from "../../components/ForumTypeBadge";
import Skeleton from "../../components/Skeleton";
import { cleanTitle, getDisplayForum } from "../../utils/cleaners";
import { CANONICAL_SECTORS, normalizeSectorName, getAllCanonicalDisplayNames } from "../../utils/sectorRegistry";
import { useDomainProfile } from "../../hooks/useDomainProfile";
import { profileStore } from "../../store/profileStore";
import { domainStore } from "../../store/domainStore";
import { ScanningRadarPanel } from "../../components/ScanningRadarPanel";
import { WatchlistControlPanel } from "../../components/WatchlistControlPanel";

const ALL_SECTOR_DISPLAY_NAMES = getAllCanonicalDisplayNames();


const highlightDomain = (text: string, domain: string) => {
  if (!text) return "";
  const parts = text.split(new RegExp(`(${domain.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&')})`, 'gi'));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === domain.toLowerCase() ? (
          <span key={i} style={{ color: '#ef9f27', fontWeight: 'bold', backgroundColor: 'rgba(239, 159, 39, 0.15)', padding: '1px 3px', borderRadius: '2px' }}>
            {part}
          </span>
        ) : part
      )}
    </>
  );
};

const FindingMatchViewer = ({ leak, activeDomain }: { leak: any, activeDomain: string }) => {
  const [expanded, setExpanded] = useState(false);
  const snippets = leak.match_snippets || [];
  if (snippets.length === 0) return null;
  
  const first = snippets[0];
  const rest = snippets.slice(1);
  
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "4px" }}>
      <div style={{ background: "var(--color-bg)", border: "1px solid var(--card-border)", borderRadius: "4px", padding: "10px" }}>
        <div style={{ fontSize: "9px", fontWeight: 700, color: "var(--color-text-subtle)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>
          MATCHES IN: {first.field}
        </div>
        <div style={{ fontSize: "12px", fontFamily: "monospace", color: "var(--color-text)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}>
          {highlightDomain(first.snippet, activeDomain)}
        </div>
      </div>
      
      {expanded && rest.map((h: any, idx: number) => (
        <div key={idx} style={{ background: "var(--color-bg)", border: "1px solid var(--card-border)", borderRadius: "4px", padding: "10px" }}>
          <div style={{ fontSize: "9px", fontWeight: 700, color: "var(--color-text-subtle)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "6px" }}>
            MATCHES IN: {h.field}
          </div>
          <div style={{ fontSize: "12px", fontFamily: "monospace", color: "var(--color-text)", whiteSpace: "pre-wrap", wordBreak: "break-word", lineHeight: 1.5 }}>
            {highlightDomain(h.snippet, activeDomain)}
          </div>
        </div>
      ))}
      
      {rest.length > 0 && (
        <button onClick={() => setExpanded(!expanded)} style={{ alignSelf: "flex-start", background: "transparent", border: "none", color: "#2d7dd2", fontSize: "11px", fontWeight: 600, cursor: "pointer", padding: "2px 0" }}>
          {expanded ? "Show less" : `Show ${rest.length} more match${rest.length > 1 ? 'es' : ''}`}
        </button>
      )}
    </div>
  );
};

const AnimatedStatCard = ({ target, suffix = "", duration = 2000, prefix = "", description }: { target: number, suffix?: string, duration?: number, prefix?: string, description: string }) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let startTimestamp: number | null = null;
    let animationFrame: number;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeOut = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      setVal(Math.floor(easeOut * target));
      if (progress < 1) {
        animationFrame = window.requestAnimationFrame(step);
      }
    };
    animationFrame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [target, duration]);
  
  // Format large numbers
  const displayVal = val >= 1000000 ? (val / 1000000).toFixed(1) : val.toLocaleString();
  const displaySuffix = val >= 1000000 ? "M+" : suffix;

  return (
    <div style={{ background: "var(--color-bg)", padding: "28px 24px", borderRadius: "12px", border: "1px solid var(--card-border)", textAlign: "center", boxShadow: "0 8px 24px rgba(0,0,0,0.06)", display: "flex", flexDirection: "column", gap: "10px", alignItems: "center", transition: "transform 0.2s" }} className="hover:-translate-y-1">
      <div style={{ fontSize: "36px", fontWeight: "900", color: "var(--color-text)", letterSpacing: "-0.03em", lineHeight: 1 }}>
        {prefix}{displayVal}{displaySuffix}
      </div>
      <div style={{ fontSize: "14px", color: "var(--color-text-subtle)", fontWeight: 500, lineHeight: 1.4 }}>{description}</div>
    </div>
  );
};

export default function DomainMonitorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFreeUser = isFreePlanUser(user);

  const location = useLocation();
  useEffect(() => {
    const urlDomain = new URLSearchParams(location.search).get("domain");
    if (!urlDomain) {
      const cachedDomain = sessionStorage.getItem('dm_domainInput');
      if (cachedDomain) {
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("domain", cachedDomain);
        window.history.replaceState({}, "", newUrl.toString());
      } else {
        setSearchResult(null);
        setDomainInput("");
                setModalSector("");
        setModalPeriod("30D");
      }
    }
  }, [location.search]);


  // URL Tab State: ?tab=darknet|company|sector
  const [activeTab, setActiveTab] = useState<"darknet" | "company" | "sector">("darknet");

  // View Mode: search vs continuous surveillance
  const [viewMode, setViewMode] = useState<"search" | "continuous">(() => {
    const params = new URLSearchParams(window.location.search);
    const qv = params.get("view");
    return qv === "continuous" || qv === "watchlist" ? "continuous" : "search";
  });

  // Radar scanning stages & countdown
  const [scanStageIndex, setScanStageIndex] = useState<number>(0);
  const [isFinalizing, setIsFinalizing] = useState<boolean>(false);

  // Search Input & Clean Domain State
  const [domainInput, setDomainInput] = useState(() => sessionStorage.getItem('dm_domainInput') || "");
  const [includeSubdomains, setIncludeSubdomains] = useState(true);
  const [inputError, setInputError] = useState<string | null>(null);

  // Search Results & Pipeline State

  const [searchResult, setSearchResult] = useState<DomainSearchResult | null>(() => { try { const s = sessionStorage.getItem('dm_searchResult'); return s ? JSON.parse(s) : null; } catch { return null; } });
  const [searchError, setSearchError] = useState<string | null>(null);
  const [cappedCount, setCappedCount] = useState<{ total: number; formatted: string; is_capped: boolean } | null>(() => { try { const s = sessionStorage.getItem('dm_cappedCount'); return s ? JSON.parse(s) : null; } catch { return null; } });
  const [scanCompletedAt, setScanCompletedAt] = useState<Date | null>(() => { const s = sessionStorage.getItem('dm_scanCompletedAt'); return s ? new Date(s) : null; });
  const abortControllerRef = useRef<AbortController | null>(null);

  const [pipelineState, setPipelineState] = useState<{
    db: "waiting" | "running" | "slow" | "done" | "timed_out" | "failed" | "skipped";
    mentions: "waiting" | "running" | "slow" | "done" | "timed_out" | "busy" | "failed" | "skipped";
    web: "waiting" | "running" | "slow" | "done" | "timed_out" | "failed" | "skipped";
    sector: "waiting" | "running" | "slow" | "done" | "failed" | "skipped";
  }>(() => { try { const s = sessionStorage.getItem('dm_pipelineState'); return s ? JSON.parse(s) : { db: "waiting", mentions: "waiting", web: "waiting", sector: "waiting" }; } catch { return { db: "waiting", mentions: "waiting", web: "waiting", sector: "waiting" }; } });

  const [pipelineElapsed, setPipelineElapsed] = useState(0);
  const [scanProgress, setScanProgress] = useState(0);
  const [globalStats, setGlobalStats] = useState({ total_leaks: 25500000, total_actors: 85000, total_countries: 120 });




  useEffect(() => {
    if (domainInput) sessionStorage.setItem('dm_domainInput', domainInput);
    if (searchResult) {
      sessionStorage.setItem('dm_searchResult', JSON.stringify(searchResult));
      sessionStorage.setItem('dm_pipelineState', JSON.stringify(pipelineState));
      if (cappedCount) sessionStorage.setItem('dm_cappedCount', JSON.stringify(cappedCount));
      if (scanCompletedAt) sessionStorage.setItem('dm_scanCompletedAt', scanCompletedAt.toISOString());
    } else {
      sessionStorage.removeItem('dm_searchResult');
      sessionStorage.removeItem('dm_pipelineState');
      sessionStorage.removeItem('dm_cappedCount');
      sessionStorage.removeItem('dm_scanCompletedAt');
    }
  }, [domainInput, searchResult, pipelineState, cappedCount, scanCompletedAt]);

  const activeDomain = useMemo(() => {
    return profileStore.normalizeDomain(domainInput || searchResult?.domain || "");
  }, [domainInput, searchResult]);

  // Watchlist, History & Domain Reports
  const { watchlist, history } = useSyncExternalStore(domainStore.subscribe, domainStore.getSnapshot);
  const [domainReports, setDomainReports] = useState<ExistingReport[]>([]);

  // Canonical watched status and monitoring mode
  const watchedEntry = useMemo(() => {
    if (!activeDomain) return null;
    const clean = activeDomain.toLowerCase().trim();
    return watchlist.find(
      (w) =>
        (w.domain || "").toLowerCase() === clean ||
        ((w as any).host || "").toLowerCase() === clean ||
        ((w as any).canonical_domain || "").toLowerCase() === clean
    );
  }, [watchlist, activeDomain]);

  const isWatched = Boolean(watchedEntry);
  const isContinuousMonitored =
    watchedEntry?.source === "client_profile" || watchedEntry?.source === "continuous";

  const { profileState, startResolve, retryResolve } = useDomainProfile(activeDomain);

  const isSearchRunning = pipelineState.db === "running" || pipelineState.mentions === "running" || profileState.status === "resolving";
  const searchLoading = isSearchRunning;

  // --- PHASE 1: LOADING GATE ---
  const profileFinal = profileState.status === "ready" || profileState.status === "not_found" || profileState.status === "error";
  const scanFinal = pipelineState.db !== "running" && pipelineState.mentions !== "running" && pipelineState.db !== "waiting" && pipelineState.mentions !== "waiting";
  
  const [gateElapsed, setGateElapsed] = useState(0);
  const [userForcedEscape, setUserForcedEscape] = useState(false);
  const [profileFinalTime, setProfileFinalTime] = useState<number | null>(null);

  useEffect(() => {
    let t: any;
    if (searchLoading && !userForcedEscape) {
      t = setInterval(() => setGateElapsed(p => p + 1000), 1000);
    } else if (!searchLoading) {
      setGateElapsed(0);
      setProfileFinalTime(null);
    }
    return () => clearInterval(t);
  }, [searchLoading, userForcedEscape]);

  useEffect(() => {
    if (profileFinal && searchLoading && !profileFinalTime) {
      setProfileFinalTime(gateElapsed);
    }
  }, [profileFinal, searchLoading, gateElapsed]);

  const show45sEscape = gateElapsed >= 45000 && !userForcedEscape && searchLoading;
  const show60sEscape = gateElapsed >= 60000 && !userForcedEscape && searchLoading;
  
  const naturalReveal = profileFinal && scanFinal;
  const tenSecondsAfterProfile = profileFinalTime !== null && (gateElapsed - profileFinalTime >= 10000);
  
  const isSnapshot = !!(searchResult && (searchResult as any).is_snapshot);
  const isCachedProfile = profileState.status === "ready" && profileState.cached;
  const revealReady = naturalReveal || userForcedEscape || tenSecondsAfterProfile || isSnapshot || isCachedProfile;
  const staggerStyle = (index: number) => {
    if (!revealReady) return { opacity: 1, pointerEvents: 'none' as const };
    return {
      opacity: 1,
      animation: `fadeUp 300ms ease-out ${index * 60}ms both`
    };
  };

  useEffect(() => {
    if (searchLoading) {
      api.get("/status/").then(res => {
        if (res.data && res.data.total_leaks) {
          setGlobalStats(prev => ({ ...prev, total_leaks: res.data.total_leaks }));
        }
      }).catch(() => {});
    }
  }, [searchLoading]);

  useEffect(() => {
    if (searchLoading) {
      setScanProgress(0);
      const interval = setInterval(() => {
        setScanProgress(p => {
          const isAlmostDone = pipelineState.db === "done" && pipelineState.mentions === "done";
          const max = isAlmostDone ? 99 : 85;
          const step = isAlmostDone ? 2 : (Math.random() * 4 + 1);
          return Math.min(p + step, max);
        });
      }, 400);
      return () => clearInterval(interval);
    } else {
      setScanProgress(100);
    }
  }, [searchLoading, pipelineState]);

  // Radar Stage progression & countdown trigger
  useEffect(() => {
    if (searchLoading && !revealReady) {
      setScanStageIndex(0);
      setIsFinalizing(false);

      const t1 = setTimeout(() => setScanStageIndex(1), 700);
      const t2 = setTimeout(() => setScanStageIndex(2), 2000);
      const t3 = setTimeout(() => setScanStageIndex(3), 3600);
      const t4 = setTimeout(() => setScanStageIndex(4), 5200);
      const t5 = setTimeout(() => setScanStageIndex(5), 7000);
      const t6 = setTimeout(() => {
        setScanStageIndex(6);
        setIsFinalizing(true);
      }, 8800);

      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        clearTimeout(t4);
        clearTimeout(t5);
        clearTimeout(t6);
      };
    } else if (revealReady || !searchLoading) {
      setScanStageIndex(6);
      setIsFinalizing(true);
    }
  }, [searchLoading, revealReady]);

  // Watchdog
  useEffect(() => {
    if (!isSearchRunning) return;
    const t = setTimeout(() => {
      console.error("Watchdog timeout triggered for stuck busy state");
      // Force idle logic here if needed
    }, 60000);
    return () => clearTimeout(t);
  }, [isSearchRunning]);

  
  // Phase 2: Logout and user-change hygiene
  useEffect(() => {
    if (user) {
      setDomainInput("");
      setSearchResult(null);
      setDomainReports([]);
      domainStore.resetUserScopedState();
      profileStore.resetProfile(activeDomain || searchResult?.domain || domainInput);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    }
  }, [user?.id]);

  // Popover / Dropdown / Menu States
  const [showSourcesPopover, setShowSourcesPopover] = useState(false);
  const [showSectorDropdown, setShowSectorDropdown] = useState(false);
  const [showOverflowMenu, setShowOverflowMenu] = useState(false);

  // Findings Filters & Sorting
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const [networkFilter, setNetworkFilter] = useState<string | null>(null);
  const [textFilter, setTextFilter] = useState("");
  const [sortOrder, setSortOrder] = useState<"newest" | "highest_severity">("newest");
  const [revealedExcerpts, setRevealedExcerpts] = useState<Record<string, boolean>>({});
  const [displayLimit, setDisplayLimit] = useState(20);

  // Sector Facts State
  const [selectedPeriod, setSelectedPeriod] = useState<"30D" | "90D" | "LAST_QUARTER">("30D");
  const [comparePrevPeriod, setComparePrevPeriod] = useState(true);
  const [sectorFacts, setSectorFacts] = useState<SectorFacts | null>(null);
  const [sectorFacts7D, setSectorFacts7D] = useState<SectorFacts | null>(null);
  const [factsLoading, setFactsLoading] = useState(false);
  const [factsError, setFactsError] = useState<string | null>(null);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState<string | null>(null);
  const [selectedCountryFilter, setSelectedCountryFilter] = useState<string | null>(null);

  // Admin Diagnostics Status
  const [adminStatus, setAdminStatus] = useState<AdminIntegrationStatus | null>(null);

  // Report Modal & Active Job State
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [modalPeriod, setModalPeriod] = useState<"30D" | "90D" | "LAST_QUARTER">("30D");
  const [modalSector, setModalSector] = useState("");
  const [activeJob, setActiveJob] = useState<ReportJob | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [dedupeJob, setDedupeJob] = useState<ReportJob | null>(null);
  const [canRegenerate, setCanRegenerate] = useState(false);
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [activeReportJob, setActiveReportJob] = useState<ReportJob | null>(null);
  const [isAddDomainModalOpen, setIsAddDomainModalOpen] = useState(false);

  // ── Task D: Canonical plural helper ──────────────────────────────────────
  const plural = (n: number, singular: string, pluralForm?: string): string => {
    const word = n === 1 ? singular : (pluralForm ?? `${singular}s`);
    return `${n} ${word}`;
  };

  // Restore active job on mount
  useEffect(() => {
    fetchActiveReportJob().then(job => {
      if (job && (job.status === "queued" || job.status === "running")) {
        setActiveReportJob(job);
      }
    }).catch(() => {});
  }, []);


  // Tab scroll position memory
  const tabScrollPositions = useRef<{ darknet: number; company: number; sector: number }>({
    darknet: 0,
    company: 0,
    sector: 0,
  });

  const sourcesRef = useRef<HTMLDivElement>(null);
  const sectorDropdownRef = useRef<HTMLDivElement>(null);
  const overflowMenuRef = useRef<HTMLDivElement>(null);

  // Initial Sync from URL & Storage
  useEffect(() => {
    fetchInitialData();
    if (user?.role === "admin" || user?.role === "analyst") {
      fetchAdminIntegrationStatus().then(setAdminStatus).catch(() => {});
    }

    const params = new URLSearchParams(window.location.search);
    const qTab = params.get("tab");
    if (qTab === "company" || qTab === "sector" || qTab === "darknet") {
      setActiveTab(qTab);
    }

    const qDomain = params.get("domain");
    if (qDomain) {
      setDomainInput(qDomain);
      loadDomain(qDomain, { source: 'url' });
    } else if (user?.id) {
      try {
        const markerStr = localStorage.getItem(`tx.dm.last.${user.id}`);
        if (markerStr) {
          const marker = JSON.parse(markerStr);
          const currentLoginSession = localStorage.getItem("tx_login_session");
          const isValid = marker.userId === user.id && 
                          marker.loginSessionId === currentLoginSession &&
                          (Date.now() - marker.savedAt) < 12 * 60 * 60 * 1000;
          if (isValid && marker.domain) {
            setDomainInput(marker.domain);
            loadDomain(marker.domain, { source: 'restore' });
          } else {
            localStorage.removeItem(`tx.dm.last.${user.id}`);
          }
        }
      } catch (e) {
        localStorage.removeItem(`tx.dm.last.${user?.id}`);
      }
    }
  }, []);

  // Sync activeTab to URL search params
  const handleTabChange = (newTab: "darknet" | "company" | "sector") => {
    tabScrollPositions.current[activeTab] = window.scrollY;
    setActiveTab(newTab);
    const url = new URL(window.location.href);
    url.searchParams.set("tab", newTab);
    window.history.replaceState({}, "", url.toString());

    setTimeout(() => {
      window.scrollTo({ top: tabScrollPositions.current[newTab] || 0, behavior: "instant" });
    }, 10);
  };

  // Fetch sector facts whenever profile sector or period changes
  useEffect(() => {
    const sec = profileState.profile?.sector;
    if (sec) {
      const normSec = normalizeSectorName(sec);
      setModalSector(normSec);
      fetchFactsForSector(normSec, selectedPeriod);
    }
  }, [profileState.profile?.sector, selectedPeriod]);

  // Sync web and sector pipeline states
  useEffect(() => {
    setPipelineState((prev) => {
      let nextWeb = prev.web;
      let nextSector = prev.sector;

      if (profileState.status === "ready" || profileState.status === "low_confidence" || profileState.status === "not_found") {
        nextWeb = "done";
      } else if (profileState.status === "error" || profileState.status === "timeout") {
        nextWeb = "failed";
      } else if (profileState.status === "starting" || profileState.status === "resolving") {
        nextWeb = "running";
      }

      if (factsLoading) {
        nextSector = "running";
      } else if (factsError) {
        nextSector = "failed";
      } else if (sectorFacts) {
        nextSector = "done";
      }

      if (prev.web === nextWeb && prev.sector === nextSector) return prev;
      return { ...prev, web: nextWeb, sector: nextSector };
    });
  }, [profileState.status, factsLoading, factsError, sectorFacts]);

  // Fetch domain report history
  useEffect(() => {
    if (activeDomain) {
      fetchDomainReports(activeDomain).then(setDomainReports).catch(() => {});
    }
  }, [activeDomain, activeJob?.status]);

  // Poll active report job status
  useEffect(() => {
    checkActiveJob();
    const interval = setInterval(() => {
      if (!document.hidden) {
        checkActiveJob();
      }
    }, activeJob ? 1500 : 10000);

    return () => clearInterval(interval);
  }, [activeJob?.status]);

  // Timer for active job elapsed time
  useEffect(() => {
    let timer: any = null;
    if (activeJob && (activeJob.status === "queued" || activeJob.status === "running")) {
      timer = setInterval(() => {
        setElapsedTime((prev) => prev + 1);
      }, 1000);
    } else {
      setElapsedTime(0);
    }
    return () => clearInterval(timer);
  }, [activeJob?.status]);

  // Click outside listener for popovers
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (sourcesRef.current && !sourcesRef.current.contains(e.target as Node)) {
        setShowSourcesPopover(false);
      }
      if (sectorDropdownRef.current && !sectorDropdownRef.current.contains(e.target as Node)) {
        setShowSectorDropdown(false);
      }
      if (overflowMenuRef.current && !overflowMenuRef.current.contains(e.target as Node)) {
        setShowOverflowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const fetchInitialData = async () => {
    try {
      await Promise.all([domainStore.fetchWatchlist(), domainStore.fetchHistory()]);
    } catch (err) {
      console.error("Failed to load initial monitor data", err);
    }
  };

    

  const checkActiveJob = async () => {
    try {
      const job = await fetchActiveReportJob();
      if (job) {
        setActiveJob(job);
        setActiveReportJob(job);
      } else {
        if (activeJob || activeReportJob) {
          setActiveJob(null);
          setActiveReportJob(null);
          if (activeDomain) {
            fetchDomainReports(activeDomain).then(setDomainReports).catch(() => {});
          }
          toast.success("Executive domain report ready!", {
            description: "Click Download to inspect your PDF deliverable."
          });
        }
      }
    } catch (err) {}
  };

  const validateInput = (input: string): string | null => {
    const raw = input.trim().toLowerCase();
    if (!raw) return "Please enter a corporate domain name.";
    const clean = raw.replace(/^(https?:\/\/)?(www\.)?/, "").replace(/\[\.\]/g, ".").split("/")[0];
    if (clean.length > 253) return "Domain name is too long (max 253 characters).";
    if (/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(clean)) return "IP addresses are not valid corporate domains.";
    if (clean === "localhost" || clean.endsWith(".local") || clean.endsWith(".internal"))
      return "Localhost and internal domains are not permitted.";
    if (!clean.includes(".")) return "Domain must contain a valid TLD extension (e.g. company.com).";
    return null;
  };

  const cancelScan = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
        setPipelineState((prev) => ({
      ...prev,
      db: prev.db === "running" || prev.db === "slow" ? "failed" : prev.db,
      mentions: prev.mentions === "running" || prev.mentions === "slow" ? "failed" : prev.mentions,
    }));
    toast.info("Domain search cancelled by user.");
  };


  const loadDomain = async (domainToSearch: string, options: { source: "search" | "recent" | "watchlist" | "restore" | "url" } = { source: "search" }) => {
    const valErr = validateInput(domainToSearch);
    if (valErr) {
      setInputError(valErr);
      return;
    }
    setInputError(null);
    setSearchError(null);
    setCappedCount(null);
    setScanCompletedAt(null);

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    const cleanDomain = domainToSearch
      .trim()
      .replace(/^(https?:\/\/)?(www\.)?/, "")
      .replace(/\[\.\]/g, ".")
      .split("/")[0];

    const isRestore = options.source === "restore";

    if (!isRestore) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("domain", cleanDomain);
      window.history.pushState({}, "", newUrl.toString());
      
      if (user?.id) {
         const sid = localStorage.getItem("tx_login_session");
         localStorage.setItem(`tx.dm.last.${user.id}`, JSON.stringify({
            domain: cleanDomain,
            includeSubdomains,
            savedAt: Date.now(),
            userId: user.id,
            loginSessionId: sid
         }));
      }
    }

    setDomainInput(cleanDomain);

    setPipelineState({ db: "running", mentions: "running", web: "running", sector: "running" });

    // Initial result holder
    setSearchResult({
      domain: cleanDomain,
      total_matches: 0,
      results: [],
      direct_results: [],
      mentions_results: [],
      direct_status: "skipped",
      mentions_status: "skipped",
      scan_duration_ms: 0,
      posts_scanned: 6191,
      severity_counts: { critical: 0, high: 0, medium: 0, low: 0 },
      source_counts: {},
      network_counts: { tor: 0, telegram: 0, clearweb: 0, other: 0 },
      risk_rating: { level: "MINIMAL", score: 5, rule_text: "Scanning in progress..." },
    });

    // Check if it's a known stored profile (optimistic read for instant reveal)
    let isStoredAndReady = false;
    let storedProfile = null;
    try {
        const cached = profileStore.getState(cleanDomain);
        if (cached.status === "ready" && cached.profile) {
            isStoredAndReady = true;
            storedProfile = cached.profile;
        } else {
            // Check status API
            const statRes = await api.get(`/report-center/profile/status?domain=${encodeURIComponent(cleanDomain)}`);
            if (statRes.data.status === "ready" && statRes.data.profile) {
                isStoredAndReady = true;
                storedProfile = statRes.data.profile;
            } else if (statRes.data.status === "not_found") {
                isStoredAndReady = true; // Not found is also a final state
                storedProfile = statRes.data.profile;
            }
        }
    } catch (e) {}

    if (isStoredAndReady && storedProfile) {
        // FAST PATH: Stored and ready -> instant reveal
        if (storedProfile.sector) {
            const normSec = normalizeSectorName(storedProfile.sector);
            setModalSector(normSec);
            fetchFactsForSector(normSec, selectedPeriod); // fetch facts in background
        } else {
            setModalSector("");
            setSectorFacts(null);
            setSectorFacts7D(null);
        }
        
        // Update profileStore to immediately reflect ready state
        profileStore.updateProfileSector(cleanDomain, storedProfile);
    } else {
        // Clear stale sector for fresh search
        setModalSector("");
        setSectorFacts(null);
        setSectorFacts7D(null);
    }

    
    if (isRestore) {
        // Reconnect to status polling, no Serper call
        import("../../store/profileStore").then(({ profileStore }) => profileStore.reconnect(cleanDomain));
    } else {
        startResolve(false, cleanDomain);
    }

    // 0. Fetch Fast Capped Total Count in Background
    fetchDomainCount(cleanDomain, includeSubdomains)
      .then((cntRes) => {
        if (!abortController.signal.aborted) {
          setCappedCount(cntRes);
        }
      })
      .catch(() => {});

    // 1. Direct DB Matches
    const directPromise = searchDomainPhase(cleanDomain, includeSubdomains, "direct", abortController.signal, isRestore)
      .then((res: any) => {
        if (abortController.signal.aborted) return;
        setSearchResult((prev: any) => {
          const directItems = res.direct_results || res.results || [];
          const existingMentions = prev?.mentions_results || [];
          const combined = [...directItems, ...existingMentions];
          return {
            ...prev,
            ...res,
            results: combined,
            direct_results: directItems,
            mentions_results: existingMentions,
            direct_status: "success",
            scan_duration_ms: (prev?.scan_duration_ms || 0) + (res.scan_duration_ms || 0),
          };
        });
        setPipelineState((prev) => ({ ...prev, db: "success" }));
      })
      .catch((err: any) => {
        if (abortController.signal.aborted) return;
        setPipelineState((prev) => ({ ...prev, db: "error" }));
      });

    // 2. Mentions DB Matches
    const mentionsPromise = searchDomainPhase(cleanDomain, includeSubdomains, "mentions", abortController.signal, isRestore)
      .then((res: any) => {
        if (abortController.signal.aborted) return;
        setSearchResult((prev: any) => {
          const mentionsItems = res.mentions_results || res.results || [];
          const existingDirect = prev?.direct_results || [];
          const combined = [...existingDirect, ...mentionsItems];
          return {
            ...prev,
            ...res,
            results: combined,
            direct_results: existingDirect,
            mentions_results: mentionsItems,
            mentions_status: "success",
            scan_duration_ms: (prev?.scan_duration_ms || 0) + (res.scan_duration_ms || 0),
          };
        });
        setPipelineState((prev) => ({ ...prev, mentions: "success" }));
      })
      .catch((err: any) => {
        if (abortController.signal.aborted) return;
        setPipelineState((prev) => ({ ...prev, mentions: "error" }));
      });

    Promise.all([directPromise, mentionsPromise])
      .then(() => {
        if (abortController.signal.aborted) return;
        setScanCompletedAt(new Date());
              });
  };

  const retryDirectPhase = async () => {
    if (!activeDomain) return;
    setPipelineState((prev) => ({ ...prev, db: "running" }));
    try {
      const res = await searchDomainPhase(activeDomain, includeSubdomains, "direct");
      setPipelineState((prev) => ({ ...prev, db: "done" }));
      setSearchResult((prev) => {
        const directItems = res.direct_results || res.results || [];
        const existingMentions = prev?.mentions_results || [];
        const combined = [...directItems, ...existingMentions];
        return {
          ...(prev || res),
          total_matches: combined.length,
          direct_results: directItems,
          direct_status: "success",
          results: combined,
        };
      });
      toast.success("Direct database phase retried successfully.");
    } catch (err) {
      setPipelineState((prev) => ({ ...prev, db: "failed" }));
      toast.error("Direct phase retry failed.");
    }
  };

  const retryMentionsPhase = async () => {
    if (!activeDomain) return;
    setPipelineState((prev) => ({ ...prev, mentions: "running" }));
    try {
      const res = await searchDomainPhase(activeDomain, includeSubdomains, "mentions");
      setPipelineState((prev) => ({ ...prev, mentions: res.busy ? "busy" : "done" }));
      setSearchResult((prev) => {
        const mentionsItems = res.mentions_results || res.results || [];
        const existingDirect = prev?.direct_results || [];
        const combined = [...existingDirect, ...mentionsItems];
        return {
          ...(prev || res),
          total_matches: combined.length,
          mentions_results: mentionsItems,
          mentions_status: res.busy ? "busy" : "success",
          mentions_duration_ms: res.mentions_duration_ms,
          results: combined,
        };
      });
      toast.success("Mentions phase retried successfully.");
    } catch (err) {
      setPipelineState((prev) => ({ ...prev, mentions: "failed" }));
      toast.error("Mentions phase retry failed.");
    }
  };

  const fetchFactsForSector = async (sector: string, period: "30D" | "90D" | "LAST_QUARTER") => {
    setFactsLoading(true);
    setFactsError(null);
    try {
      const normSector = normalizeSectorName(sector);
      const [facts, facts7D] = await Promise.all([
        fetchSectorFacts(normSector, period),
        fetchSectorFacts(normSector, "7D")
      ]);
      setSectorFacts(facts);
      setSectorFacts7D(facts7D);
    } catch (err) {
      setFactsError("Unable to load sector threat intelligence facts.");
    } finally {
      setFactsLoading(false);
    }
  };

  const handleSectorChange = async (newSector: string) => {
    const normSector = normalizeSectorName(newSector);
    setShowSectorDropdown(false);
    try {
      const currentProf = profileState.profile;
      const domainToConfirm = currentProf?.domain || activeDomain;
      const companyName = currentProf?.company_name || activeDomain.split(".")[0].toUpperCase();
      const updated = await confirmCompanyProfile(domainToConfirm, normSector, companyName);
      profileStore.updateProfileSector(domainToConfirm, updated);
      setModalSector(normSector);
      fetchFactsForSector(normSector, selectedPeriod);
      toast.success(`Sector updated to ${normSector}`);
    } catch (err) {
      toast.error("Failed to update sector");
    }
  };

  const handleWatchlistToggle = async (overrideTarget?: string) => {
    const target = overrideTarget || searchResult?.domain || activeDomain;
    if (!target) return;
    
    // Check if target is already watched
    const targetClean = target.toLowerCase().trim();
    const currentlyWatched = watchlist.some(w => w.domain === targetClean || w.canonical_domain === targetClean);
    
    // Optimistic update
    const previousWatchlist = [...watchlist];
    if (currentlyWatched) {
       setWatchlist(prev => prev.filter(w => w.domain !== targetClean && w.canonical_domain !== targetClean));
    } else {
       setWatchlist(prev => [{
           domain: targetClean,
           canonical_domain: targetClean,
           hits: searchResult?.total_matches || 0,
           source: 'manual',
           added_at: new Date().toISOString(),
           last_scan_at: new Date().toISOString()
       } as any, ...prev]);
    }
    
    try {
      if (currentlyWatched) {
        await removeFromWatchlist(target);
        toast.success("Undo", {
          action: {
            label: 'Undo',
            onClick: () => handleWatchlistToggle(target)
          }
        });
      } else {
        await addToWatchlist(target);
      }
      // Re-sync quietly
      getWatchlist().then(setWatchlist).catch(() => {});
    } catch (err: any) {
      // Rollback
      setWatchlist(previousWatchlist);
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Watchlist update failed";
      toast.error(typeof msg === 'string' ? msg : "An error occurred.");
    }
  };

  const handleStartReport = async () => {
    const targetDomain = profileState.profile?.domain || searchResult?.domain || activeDomain;
    if (!targetDomain) {
      toast.error("Please select or search a domain first.");
      return;
    }
    const rawTargetSector = modalSector || profileState.profile?.sector;
  const targetSector = (!revealReady || (revealReady && !scanFinal && !userForcedEscape)) ? null : rawTargetSector;
    setReportSubmitting(true);
    try {
      const res: any = await createReportJob(targetDomain, targetSector, modalPeriod, "pdf_v2");
      if (res.dedupe_hit) {
        setDedupeJob(res.existing_job);
        setCanRegenerate(res.can_regenerate);
        if (res.can_regenerate) {
          toast.info("A report for this domain and parameters already exists.", {
            description: "You can view the existing one or force a new generation.",
            duration: 10000,
            action: {
              label: "Force Regenerate",
              onClick: async () => {
                setReportSubmitting(true);
                try {
                  const forceRes: any = await createReportJob(targetDomain, targetSector, modalPeriod, "pdf_v2", true);
                  setActiveJob(forceRes);
                  setShowGenerateModal(false);
                  toast.success("Regenerating report...", {
                    description: "Your new report is being prepared."
                  });
                } catch (err: any) {
                  toast.error("Failed to force regenerate.");
                } finally {
                  setReportSubmitting(false);
                }
              }
            }
          });
        } else {
          toast.info("A recent report already exists.", {
            description: "You must wait 30 minutes before generating another identical report."
          });
        }
      } else {
        setActiveJob(res);
        setShowGenerateModal(false);
        toast.success("Generating report. Please wait...", {
          description: "Your executive intelligence report is being prepared.",
          duration: 4000
        });
      }
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Failed to start report job.";
      toast.error(typeof msg === 'string' ? msg : "Failed to start report job.");
    } finally {
      setReportSubmitting(false);
    }
  };

  
  const handleClearSearch = () => {
    setDomainInput("");
    setSearchResult(null);
    if (searchResult?.domain || domainInput) {
       profileStore.resetProfile(searchResult?.domain || domainInput);
    }
    setModalSector("");
    setSectorFacts(null);
    setSectorFacts7D(null);
    if (user?.id) {
      localStorage.removeItem(`tx.dm.last.${user.id}`);
    }
    const url = new URL(window.location.href);
    url.searchParams.delete("domain");
    window.history.pushState({}, "", url.toString());
  };

  const handleCancelReport = async () => {
    if (!activeJob) return;
    try {
      await cancelReportJob(activeJob.id);
      setActiveJob(null);
      toast.info("Report generation cancelled.");
    } catch (err) {
      toast.error("Failed to cancel report job.");
    }
  };

  const handleDownloadPdf = async (jobId: string, filename?: string) => {
    try {
      const blob = await downloadReportPdf(jobId);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || `Threatactix_Report_${activeDomain}_${selectedPeriod}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("PDF Downloaded");
    } catch (err) {
      toast.error("Failed to download PDF report");
    }
  };

  // Filtered Findings List
  const filteredFindings = useMemo(() => {
    if (!searchResult?.results) return [];
    let list = [...searchResult.results];

    if (severityFilter) {
      list = list.filter((r) => r.severity.toLowerCase() === severityFilter.toLowerCase());
    }
    if (networkFilter) {
      list = list.filter((r) => r.network.toLowerCase() === networkFilter.toLowerCase());
    }
    if (textFilter.trim()) {
      const q = textFilter.toLowerCase();
      list = list.filter(
        (r) =>
          r.thread_title_list.toLowerCase().includes(q) ||
          r.forum.toLowerCase().includes(q) ||
          r.match_snippets.some((s) => s.snippet.toLowerCase().includes(q))
      );
    }

    if (sortOrder === "highest_severity") {
      const rank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
      list.sort((a, b) => (rank[b.severity.toLowerCase()] || 0) - (rank[a.severity.toLowerCase()] || 0));
    } else {
      list.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    return list;
  }, [searchResult?.results, severityFilter, networkFilter, textFilter, sortOrder]);

  const directFindings = useMemo(
    () => filteredFindings.filter((f) => f.phase === "direct" || f.match_fields.includes("website")),
    [filteredFindings]
  );
  const mentionsFindings = useMemo(
    () => filteredFindings.filter((f) => f.phase === "mentions" && !f.match_fields.includes("website")),
    [filteredFindings]
  );

  // Risk Rating Compute
  const riskRating = searchResult?.risk_rating || {
    level: searchResult?.total_matches ? "HIGH" : "MINIMAL",
    score: searchResult?.total_matches ? 75 : 5,
    rule_text: searchResult?.total_matches
      ? `${searchResult.total_matches} darknet exposures identified.`
      : "Zero darknet exposures detected across database.",
  };

  const totalMatches = searchResult?.total_matches || 0;
  const criticalCount = searchResult?.results.filter((r: any) => r.severity === "critical").length || 0;
  const highCount = searchResult?.results.filter((r: any) => r.severity === "high").length || 0;

  const getBannerDetails = () => {
    if (!searchResult) return null;
    const { total_matches, results, domain } = searchResult;
    if (total_matches === 0) {
      return {
        bg: "rgba(63, 182, 139, 0.1)", border: "rgba(63, 182, 139, 0.2)", color: "#3fb68b",
        message: `0 exposures found for ${domain}`, icon: <CheckCircle size={18} />
      };
    }
    const hasRansomware = results.some((r: any) => r.forum_type === "ransomware" || r.severity === "critical");
    const hasHighOnly = results.some((r: any) => r.severity === "high") && !hasRansomware;

    if (hasRansomware) {
      return {
        bg: "rgba(226, 75, 74, 0.1)", border: "rgba(226, 75, 74, 0.2)", color: "#e24b4a",
        message: `${total_matches} ${total_matches === 1 ? 'exposure' : 'exposures'} found for ${domain}`,
        icon: <AlertTriangle size={18} className="animate-pulse" />
      };
    }
    if (hasHighOnly) {
      return {
        bg: "rgba(239, 159, 39, 0.1)", border: "rgba(239, 159, 39, 0.2)", color: "#ef9f27",
        message: `${total_matches} ${total_matches === 1 ? 'exposure' : 'exposures'} found for ${domain}`,
        icon: <AlertCircle size={18} />
      };
    }
    return {
      bg: "rgba(45, 125, 210, 0.1)", border: "rgba(45, 125, 210, 0.2)", color: "#2d7dd2",
      message: `${total_matches} ${total_matches === 1 ? 'exposure' : 'exposures'} found for ${domain}`,
      icon: <Globe size={18} />
    };
  };
  const banner = getBannerDetails();

  const getSeverityLabel = (severity: string) => {
    switch (severity) {
      case "critical": return "CRIT";
      case "high": return "HIGH";
      case "medium": return "MED";
      default: return "LOW";
    }
  };

  
  const formatRelativeTime = (isoString: string) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hr ago`;
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    
    const dStr = date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    return dStr;
  };

  const formatTooltipDate = (isoString: string) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical": return "#e24b4a";
      case "high": return "#ef9f27";
      case "medium": return "#2d7dd2";
      default: return "#64748b";
    }
  };



  const getWatchlistBadgeStyle = (hits: number) => {
    if (hits >= 3) return { bg: "rgba(226, 75, 74, 0.12)", color: "#e24b4a", border: "1px solid rgba(226, 75, 74, 0.25)" };
    if (hits >= 1) return { bg: "rgba(239, 159, 39, 0.12)", color: "#ef9f27", border: "1px solid rgba(239, 159, 39, 0.25)" };
    return { bg: "rgba(63, 182, 139, 0.12)", color: "#3fb68b", border: "1px solid rgba(63, 182, 139, 0.25)" };
  };

  return (
    <div style={{ background: "var(--bg-app)", minHeight: "100vh", color: "var(--color-text)", padding: "24px" }} className="domain-monitor-container">
      <style>{`
        @keyframes pulse-dot { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }
        .live-dot { width: 6px; height: 6px; border-radius: 50%; background: #3fb68b; animation: pulse-dot 2s ease-in-out infinite; display: inline-block; margin-right: 4px; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @media (prefers-reduced-motion: reduce) { .domain-monitor-container * { animation: none !important; transition: none !important; } }
        
        .layout-container { display: flex; gap: 24px; max-width: 1200px; margin: 0 auto; flex-direction: column; }
        .right-rail { width: 100%; display: flex; flex-direction: column; gap: 20px; }
        @media (min-width: 1280px) {
          .layout-container { flex-direction: row; }
          .right-rail { width: 300px; flex-shrink: 0; }
        }
      `}</style>
      <div className="layout-container">
        
        {/* Main Content Column */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "20px" }}>
          
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ background: "rgba(63, 182, 139, 0.1)", padding: "10px", borderRadius: "8px", border: "1px solid rgba(63, 182, 139, 0.2)" }}>
                <Radar style={{ color: "#3fb68b" }} size={24} />
              </div>
              <div>
                <h1 style={{ fontSize: "20px", fontWeight: 700, color: "var(--color-text)", margin: 0 }}>Domain Threat Intelligence</h1>
                <p style={{ fontSize: "12px", color: "var(--color-text-muted)", margin: 0 }}>
                  24/7 continuous dark web surveillance, breach tracking & targeted exposure search.
                </p>
              </div>
            </div>

            {/* Mode Switcher Tabs */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px", background: "var(--color-bg)", padding: "4px", borderRadius: "8px", border: "1px solid var(--card-border)" }}>
              <button
                type="button"
                onClick={() => {
                  setViewMode("search");
                  const url = new URL(window.location.href);
                  url.searchParams.set("view", "search");
                  window.history.replaceState({}, "", url.toString());
                }}
                style={{
                  background: viewMode === "search" ? "rgba(16, 185, 129, 0.15)" : "transparent",
                  color: viewMode === "search" ? "#10b981" : "var(--color-text-muted)",
                  border: viewMode === "search" ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid transparent",
                  borderRadius: "6px",
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.15s ease",
                }}
              >
                <Search size={13} />
                <span>Deep Search</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setViewMode("continuous");
                  const url = new URL(window.location.href);
                  url.searchParams.set("view", "continuous");
                  window.history.replaceState({}, "", url.toString());
                }}
                style={{
                  background: viewMode === "continuous" ? "rgba(16, 185, 129, 0.15)" : "transparent",
                  color: viewMode === "continuous" ? "#10b981" : "var(--color-text-muted)",
                  border: viewMode === "continuous" ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid transparent",
                  borderRadius: "6px",
                  padding: "6px 14px",
                  fontSize: "12px",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  transition: "all 0.15s ease",
                }}
              >
                <span
                  style={{
                    width: "7px",
                    height: "7px",
                    borderRadius: "50%",
                    background: "#10b981",
                    boxShadow: "0 0 6px #10b981",
                    display: "inline-block",
                  }}
                />
                <span>Continuous Surveillance</span>
                <span
                  style={{
                    fontSize: "10px",
                    fontWeight: 700,
                    background: "rgba(16, 185, 129, 0.2)",
                    color: "#10b981",
                    padding: "1px 6px",
                    borderRadius: "10px",
                  }}
                >
                  {watchlist.length}
                </span>
              </button>
            </div>
          </div>

          {viewMode === "continuous" && (
            <WatchlistControlPanel
              watchlist={watchlist}
              onSelectDomain={(dom) => {
                setDomainInput(dom);
                loadDomain(dom, { source: "search" });
                setViewMode("search");
                const url = new URL(window.location.href);
                url.searchParams.set("view", "search");
                url.searchParams.set("domain", dom);
                window.history.pushState({}, "", url.toString());
              }}
              onOpenAddModal={() => setIsAddDomainModalOpen(true)}
              onRefreshList={() => domainStore.fetchWatchlist()}
            />
          )}

          {viewMode === "search" && (
            <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "8px", padding: "16px" }}>
              <form onSubmit={(e) => { e.preventDefault(); loadDomain(domainInput, { source: 'search' }); }} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <div style={{ display: "flex", gap: "10px" }}>
                  <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center" }}>
                    <Globe size={14} style={{ position: "absolute", left: "12px", color: "var(--color-text-subtle)" }} />
                    <input type="text" value={domainInput} onChange={(e) => { setDomainInput(e.target.value); setInputError(null); }} placeholder="Enter domain to monitor (e.g. company.com)" style={{ width: "100%", background: "var(--color-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "8px 28px 8px 34px", fontSize: "13px", color: "var(--color-text)", outline: "none" }} />
                      {domainInput && (
                        <button type="button" onClick={handleClearSearch} style={{ position: "absolute", right: "8px", background: "none", border: "none", color: "var(--color-text-subtle)", cursor: "pointer", padding: "4px" }}>
                          <X size={14} />
                        </button>
                      )}
                  </div>
                  <button type="submit" disabled={searchLoading} style={{ background: "#3fb68b", color: "#0d1117", fontWeight: 600, fontSize: "13px", padding: "8px 20px", borderRadius: "6px", border: "none", cursor: searchLoading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "6px", opacity: searchLoading ? 0.7 : 1 }}>
                    {searchLoading ? <RefreshCw size={14} className="animate-spin" /> : <Search size={14} />}
                    {searchLoading ? "Searching..." : "Search"}
                  </button>
                </div>

                {inputError && (
                  <div style={{ fontSize: "12px", color: "#e24b4a", display: "flex", alignItems: "center", gap: "4px" }}>
                    <AlertCircle size={12} /> {inputError}
                  </div>
                )}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "11px" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", color: "var(--color-text-muted)" }}>
                    <input type="checkbox" checked={includeSubdomains} onChange={(e) => setIncludeSubdomains(e.target.checked)} style={{ accentColor: "#3fb68b" }} />
                    Include subdomains
                  </label>
                  <span style={{ color: "var(--color-text-subtle)", display: "none", '@media (min-width: 640px)': { display: "inline" } } as any}>
                    Searches website, content, HTML, source url, download links, onion links, and titles.
                  </span>
                </div>
              </form>
              
              {searchLoading && !revealReady ? (
                <ScanningRadarPanel
                  domain={domainInput || activeDomain || "Target Domain"}
                  currentStageIndex={scanStageIndex}
                  isFinalizing={isFinalizing}
                  onCancel={cancelScan}
                  hasError={Boolean(searchError)}
                  errorMessage={searchError || undefined}
                  onRetry={() => loadDomain(domainInput, { source: "search" })}
                />
              ) : null}
            </div>
          )}

          {viewMode === "search" && (!searchLoading || revealReady) && searchResult && (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              
              <div style={{ ...staggerStyle(1), background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "8px", padding: "16px", display: "flex", gap: "16px", alignItems: "flex-start", position: "relative", zIndex: 100 }}>
                 {profileState.status === "resolving" && !profileState.profile ? (
                   <div style={{ width: "100%" }}>
                     <div style={{ color: "var(--color-text-muted)", fontSize: "12px", display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}><RefreshCw size={12} className="animate-spin" /> Still identifying your company...</div>
                     <Skeleton rows={2} />
                   </div>
                 ) : profileState.profile ? (
                   <>
                     <div style={{ width: "48px", height: "48px", borderRadius: "8px", background: "rgba(45, 125, 210, 0.1)", color: "#2d7dd2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px", fontWeight: "bold" }}>
                       {profileState.profile.company_name?.charAt(0) || activeDomain?.charAt(0) || "?"}
                     </div>
                     <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
                       <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                         <h2 style={{ margin: 0, fontSize: "16px", fontWeight: 700, color: "var(--color-text)" }}>{profileState.profile.company_name || activeDomain}</h2>
                         <span style={{ fontSize: "11px", color: "var(--color-text-subtle)" }}>{activeDomain}</span>
                         {profileState.profile.country && profileState.profile.country.trim().toLowerCase() !== 'unknown' && <span style={{ fontSize: "11px", background: "var(--color-surface-2)", padding: "2px 6px", borderRadius: "4px", border: "1px solid var(--card-border)" }}>{profileState.profile.country}</span>}
                         
                         {activeDomain && (
                           <button
                             onClick={() => {
                               if (isWatched) {
                                 domainStore.removeDomain(activeDomain).then(() => {
                                   toast.success(isContinuousMonitored ? "Removed from continuous monitoring" : "Removed from watchlist");
                                 }).catch(() => toast.error("Failed to update domain status"));
                               } else {
                                 setIsAddDomainModalOpen(true);
                               }
                             }}
                             title={
                               isContinuousMonitored
                                 ? "Active 24/7 continuous monitoring — click to remove"
                                 : isWatched
                                 ? "On manual watchlist — click to remove"
                                 : "Add this domain to continuous monitoring or watchlist"
                             }
                             style={{
                               marginLeft: "8px",
                               background: isContinuousMonitored
                                 ? "rgba(45, 210, 96, 0.12)"
                                 : isWatched
                                 ? "rgba(63, 182, 139, 0.12)"
                                 : "var(--color-surface-2)",
                               color: isContinuousMonitored
                                 ? "#2dd260"
                                 : isWatched
                                 ? "#3fb68b"
                                 : "var(--color-text)",
                               border: `1px solid ${
                                 isContinuousMonitored
                                   ? "rgba(45, 210, 96, 0.3)"
                                   : isWatched
                                   ? "rgba(63, 182, 139, 0.3)"
                                   : "var(--card-border)"
                               }`,
                               padding: "4px 9px",
                               borderRadius: "4px",
                               fontSize: "11px",
                               fontWeight: 500,
                               cursor: "pointer",
                               display: "flex",
                               alignItems: "center",
                               gap: "5px",
                               transition: "all 0.15s ease"
                             }}
                           >
                             {isContinuousMonitored ? (
                               <>
                                 <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#2dd260", display: "inline-block", boxShadow: "0 0 6px rgba(45, 210, 96, 0.6)" }} />
                                 <Activity size={12} className="stroke-[2.2]" />
                                 <span>Monitored (Live)</span>
                               </>
                             ) : isWatched ? (
                               <>
                                 <Star size={12} fill="currentColor" />
                                 <span>Watching</span>
                               </>
                             ) : (
                               <>
                                 <Plus size={12} />
                                 <span>Watch / Monitor</span>
                               </>
                             )}
                           </button>
                         )}
                       </div>
                       <div style={{ display: "flex", alignItems: "baseline", gap: "6px" }}>
                         <p style={{ margin: 0, fontSize: "12px", color: "var(--color-text-muted)" }}>{profileState.profile.description || "No description available."}</p>
                         <span title="Company information is automatically summarized by AI from public web search results and may occasionally be outdated." style={{ fontSize: "10px", color: "var(--color-text-subtle)", fontStyle: "italic", whiteSpace: "nowrap" }}>(Summarised from public web sources)</span>
                       </div>
                       
                       <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "8px" }}>
                         <span style={{ background: "rgba(239, 159, 39, 0.1)", color: "#ef9f27", border: "1px solid rgba(239, 159, 39, 0.2)", padding: "2px 8px", borderRadius: "12px", fontSize: "11px", fontWeight: 600 }}>
                           {profileState.profile.sector || "Other"}
                         </span>
                         
                         <span style={{ fontSize: "11px", color: "var(--color-text-subtle)", display: "flex", alignItems: "center", gap: "4px" }}>
                           Confidence: <strong style={{ color: "var(--color-text)" }}>{profileState.profile.confidence > 0.8 ? "High" : profileState.profile.confidence > 0.5 ? "Medium" : "Low"}</strong>
                         </span>

                         <div style={{ position: "relative" }}>
                           <button onClick={() => setShowSourcesPopover(!showSourcesPopover)} style={{ background: "none", border: "none", color: "#2d7dd2", fontSize: "11px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px" }}><Info size={12} /> Evidence</button>
                           {showSourcesPopover && (
                             <div ref={sourcesRef} style={{ position: "absolute", top: "100%", left: 0, background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "12px", width: "300px", zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.5)", marginTop: "8px", display: "flex", flexDirection: "column", gap: "8px" }}>
                               <h4 style={{ margin: 0, fontSize: "11px", fontWeight: "bold" }}>Sources</h4>
                               {profileState.profile.evidence?.map((ev, i) => (
                                 <div key={i} style={{ fontSize: "10px", background: "var(--color-bg)", padding: "6px", borderRadius: "4px", border: "1px solid var(--card-border)" }}>
                                   <div style={{ fontWeight: "bold", marginBottom: "2px" }}>{ev.source_domain}</div>
                                   <div style={{ color: "var(--color-text-muted)" }}>{ev.snippet}</div>
                                   {ev.url && <a href={ev.url} target="_blank" rel="noopener noreferrer" style={{ color: "#2d7dd2", textDecoration: "none", display: "flex", alignItems: "center", gap: "2px", marginTop: "4px" }}><ExternalLink size={10} /> Link</a>}
                                 </div>
                               ))}
                             </div>
                           )}
                         </div>
                       </div>
                     </div>
                     
                     <div style={{ position: "relative" }}>
                        <div style={{ fontSize: "10px", color: "var(--color-text-subtle)", marginBottom: "4px", textAlign: "right" }}>Is this your sector?</div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button onClick={() => confirmCompanyProfile(activeDomain, profileState.profile!.sector).then(()=>toast.success("Confirmed"))} style={{ background: "rgba(63, 182, 139, 0.1)", color: "#3fb68b", border: "1px solid rgba(63, 182, 139, 0.2)", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: "pointer" }}>Confirm</button>
                          <button onClick={() => setShowSectorDropdown(!showSectorDropdown)} style={{ background: "var(--color-surface-2)", color: "var(--color-text)", border: "1px solid var(--card-border)", padding: "4px 8px", borderRadius: "4px", fontSize: "11px", cursor: "pointer" }}>Change</button>
                        </div>
                        {showSectorDropdown && (
                          <div ref={sectorDropdownRef} style={{ position: "absolute", top: "100%", right: 0, background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "8px", width: "200px", zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.5)", marginTop: "4px", maxHeight: "200px", overflowY: "auto" }}>
                             {ALL_SECTOR_DISPLAY_NAMES.map(s => (
                               <button key={s} onClick={() => { confirmCompanyProfile(activeDomain, s); setShowSectorDropdown(false); toast.success("Sector updated"); }} style={{ display: "block", width: "100%", textAlign: "left", padding: "6px 8px", background: "none", border: "none", color: "var(--color-text)", fontSize: "11px", cursor: "pointer" }} className="hover:bg-slate-800 rounded">{s}</button>
                             ))}
                          </div>
                        )}
                     </div>
                   </>
                 ) : (
                   <div style={{ width: "100%", textAlign: "center", padding: "10px" }}>
                     <div style={{ fontSize: "12px", color: profileState.status === "error" ? "#e24b4a" : "var(--color-text-muted)", marginBottom: "8px" }}>
                       {profileState.status === "error" ? `Error: ${profileState.error || "Failed to resolve profile."}` : "Company profile not found. Please select your sector manually."}
                     </div>
                     {profileState.status === "error" && profileState.canRetry ? (
                       <button onClick={() => retryResolve()} style={{ background: "rgba(45, 125, 210, 0.1)", color: "#2d7dd2", border: "1px solid rgba(45, 125, 210, 0.2)", padding: "6px 12px", borderRadius: "4px", fontSize: "12px", cursor: "pointer", marginBottom: "8px" }}>
                         Retry Analysis
                       </button>
                     ) : null}
                     <div style={{ marginTop: profileState.status === "error" && profileState.canRetry ? "8px" : "0" }}>
                       <select onChange={(e) => { confirmCompanyProfile(activeDomain, e.target.value); setModalSector(e.target.value); fetchFactsForSector(e.target.value, selectedPeriod); }} style={{ background: "var(--color-bg)", border: "1px solid var(--card-border)", color: "var(--color-text)", padding: "6px 12px", borderRadius: "4px", fontSize: "12px" }}>
                         <option value="">Select sector manually...</option>
                         {ALL_SECTOR_DISPLAY_NAMES.map(s => <option key={s} value={s}>{s}</option>)}
                       </select>
                     </div>
                   </div>
                 )}
              </div>

              <div style={{ ...staggerStyle(2), display: "flex", gap: "16px", alignItems: "stretch", marginBottom: "16px", flexWrap: "wrap" }}>
{(!revealReady || (revealReady && !scanFinal && !userForcedEscape)) ? (
  <>
  <div style={{ flex: "1 1 55%", minWidth: "300px", background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "16px", display: "flex", flexDirection: "column", minHeight: "260px" }}>
    <Skeleton rows={1} width="30%" />
    <div style={{ marginTop: "16px" }}><Skeleton rows={5} /></div>
  </div>
  <div style={{ flex: "1 1 40%", minWidth: "280px", background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "16px", display: "flex", flexDirection: "column", minHeight: "260px" }}>
    <Skeleton rows={1} width="50%" />
    <div style={{ marginTop: "16px" }}><Skeleton rows={4} /></div>
  </div>
  </>
) : (
                <>
                <div style={{ flex: "1 1 55%", minWidth: "300px", background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "16px", display: "flex", flexDirection: "column" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <Activity size={16} color="#e24b4a" />
                      <h3 style={{ fontSize: "15px", fontWeight: 700, margin: 0, color: "var(--color-text)" }}>Threats in {modalSector}</h3>
                    </div>
                  </div>
                  
                  {factsLoading ? <Skeleton rows={3} /> : sectorFacts && sectorFacts7D ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "16px", flex: 1 }}>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px" }}>
                        <div style={{ background: "var(--color-bg)", border: "1px solid var(--card-border)", padding: "10px", borderRadius: "6px" }}>
                          <div style={{ fontSize: "9px", color: "var(--color-text-subtle)", textTransform: "uppercase", whiteSpace: "nowrap" }}>Ransomware (7D)</div>
                          <div style={{ fontSize: "16px", fontWeight: "bold", margin: "2px 0" }}>{sectorFacts7D.ransomware_victims}</div>
                        </div>
                        <div style={{ background: "var(--color-bg)", border: "1px solid var(--card-border)", padding: "10px", borderRadius: "6px" }}>
                          <div style={{ fontSize: "9px", color: "var(--color-text-subtle)", textTransform: "uppercase", whiteSpace: "nowrap" }}>Ransomware (30D)</div>
                          <div style={{ fontSize: "16px", fontWeight: "bold", margin: "2px 0" }}>{sectorFacts.ransomware_victims}</div>
                        </div>
                        <div style={{ background: "var(--color-bg)", border: "1px solid var(--card-border)", padding: "10px", borderRadius: "6px" }}>
                          <div style={{ fontSize: "9px", color: "var(--color-text-subtle)", textTransform: "uppercase", whiteSpace: "nowrap" }}>Data Leaks (7D)</div>
                          <div style={{ fontSize: "16px", fontWeight: "bold", margin: "2px 0" }}>{sectorFacts7D.sector_posts}</div>
                        </div>
                        <div style={{ background: "var(--color-bg)", border: "1px solid var(--card-border)", padding: "10px", borderRadius: "6px" }}>
                          <div style={{ fontSize: "9px", color: "var(--color-text-subtle)", textTransform: "uppercase", whiteSpace: "nowrap" }}>Data Leaks (30D)</div>
                          <div style={{ fontSize: "16px", fontWeight: "bold", margin: "2px 0" }}>{sectorFacts.sector_posts}</div>
                        </div>
                      </div>
                      
                      <div style={{ display: "flex", gap: "20px", flex: 1, alignItems: "center" }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                            <div style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-text-subtle)", textTransform: "uppercase" }}>Who is attacking {modalSector}</div>
                            <div style={{ display: "flex", gap: "2px", background: "var(--color-surface-2)", padding: "2px", borderRadius: "4px", border: "1px solid var(--card-border)" }}>
                              <button onClick={() => setSelectedPeriod("7D")} style={{ fontSize: "9px", padding: "2px 6px", borderRadius: "2px", border: "none", background: selectedPeriod === "7D" ? "var(--color-bg)" : "transparent", color: selectedPeriod === "7D" ? "var(--color-text)" : "var(--color-text-subtle)", cursor: "pointer" }}>7D</button>
                              <button onClick={() => setSelectedPeriod("30D")} style={{ fontSize: "9px", padding: "2px 6px", borderRadius: "2px", border: "none", background: selectedPeriod === "30D" ? "var(--color-bg)" : "transparent", color: selectedPeriod === "30D" ? "var(--color-text)" : "var(--color-text-subtle)", cursor: "pointer" }}>30D</button>
                              <button onClick={() => setSelectedPeriod("90D")} style={{ fontSize: "9px", padding: "2px 6px", borderRadius: "2px", border: "none", background: selectedPeriod === "90D" ? "var(--color-bg)" : "transparent", color: selectedPeriod === "90D" ? "var(--color-text)" : "var(--color-text-subtle)", cursor: "pointer" }}>90D</button>
                            </div>
                          </div>
                          
                          {(() => {
                            const f = selectedPeriod === "7D" ? sectorFacts7D : sectorFacts;
                            const groups = f.top_ransomware_groups || [];
                            const top3 = groups.slice(0, 3);
                            const otherCount = groups.slice(3).reduce((acc, g) => acc + g.count, 0);
                            const donutData = top3.map((g, i) => ({ label: g.name, value: g.count, color: ["#e24b4a", "#ef9f27", "#2dd260"][i] }));
                            if (otherCount > 0) donutData.push({ label: "Others", value: otherCount, color: "var(--color-surface-3)" });
                            
                            return <DonutChart data={donutData} totalLabel={donutData.length === 1 ? "Group" : "Groups"} size={120} thickness={16} />;
                          })()}
                        </div>
                      </div>
                    </div>
                  ) : <div style={{ fontSize: "12px", color: "var(--color-text-subtle)" }}>No data available</div>}
                </div>

                <div style={{ flex: "1 1 40%", minWidth: "280px", background: "rgba(99, 102, 241, 0.05)", border: "1px solid rgba(99, 102, 241, 0.3)", borderRadius: "6px", padding: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px" }}>
                    <h3 style={{ fontSize: "16px", fontWeight: 700, margin: 0, color: "#6366F1" }}>
                      {modalSector ? `Get your ${modalSector} Threat Report` : "Get your Threat Report"}
                    </h3>
                    {isFreeUser && (
                      <span style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        padding: "3px 8px",
                        borderRadius: "4px",
                        fontSize: "10px",
                        fontWeight: 700,
                        letterSpacing: "0.04em",
                        textTransform: "uppercase",
                        backgroundColor: "rgba(245, 183, 56, 0.15)",
                        border: "1px solid rgba(245, 183, 56, 0.35)",
                        color: "#f5b738",
                        whiteSpace: "nowrap"
                      }}>
                        <Gem size={11} color="#f5b738" className="stroke-[2.2]" />
                        PRO REPORT
                      </span>
                    )}
                  </div>
                  <p style={{ fontSize: "12px", color: "var(--color-text-muted)", margin: 0, lineHeight: 1.5 }}>
                    {sectorFacts && sectorFacts.ransomware_victims > 0 && modalSector && activeDomain ? (
                      <>{plural(sectorFacts.ransomware_victims, "ransomware attack")} hit {modalSector} in the last 30 days. See who is behind them and what it means for {activeDomain}.</>
                    ) : modalSector && activeDomain ? (
                      <>Get a comprehensive view of the threat landscape for {modalSector} and {activeDomain}.</>
                    ) : null}
                  </p>
                  
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px", margin: "4px 0", flex: 1 }}>
                    <div style={{ background: "rgba(99, 102, 241, 0.1)", color: "var(--color-text)", padding: "8px", borderRadius: "4px", fontSize: "11px", display: "flex", alignItems: "center", gap: "6px" }}><FileText size={12} color="#6366F1"/> PDF Export</div>
                    <div style={{ background: "rgba(99, 102, 241, 0.1)", color: "var(--color-text)", padding: "8px", borderRadius: "4px", fontSize: "11px", display: "flex", alignItems: "center", gap: "6px" }}><ShieldAlert size={12} color="#6366F1"/> Risk Rating</div>
                    <div style={{ background: "rgba(99, 102, 241, 0.1)", color: "var(--color-text)", padding: "8px", borderRadius: "4px", fontSize: "11px", display: "flex", alignItems: "center", gap: "6px" }}><Users size={12} color="#6366F1"/> Threat Actors</div>
                    <div style={{ background: "rgba(99, 102, 241, 0.1)", color: "var(--color-text)", padding: "8px", borderRadius: "4px", fontSize: "11px", display: "flex", alignItems: "center", gap: "6px" }}><FileImage size={12} color="#6366F1"/> Excerpts</div>
                  </div>
                  
                  {domainReports.length > 0 && domainReports[0].domain === activeDomain && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px", paddingTop: "12px", borderTop: "1px solid var(--card-border)" }}>
                        <span style={{ fontSize: "12px", color: "var(--color-text-subtle)", display: "flex", alignItems: "center", gap: "6px" }}>
                          <Clock size={12} />
                          <span>Last report: {new Date(domainReports[0].created_at).toLocaleDateString()}</span>
                        </span>
                        <button onClick={async () => {
                           if (isFreeUser) {
                             toast.info("Report downloads require an active Business Pro or Enterprise subscription.");
                             navigate("/subscription");
                             return;
                           }
                           const blob = await downloadReportPdf(domainReports[0].id);
                           const url = URL.createObjectURL(blob);
                           window.open(url, "_blank");
                        }} style={{ display: "flex", alignItems: "center", gap: "4px", background: "none", border: "none", color: isFreeUser ? "#f5b738" : "#6366F1", fontSize: "12px", fontWeight: 600, cursor: "pointer" }}>
                          {isFreeUser ? (
                            <>
                              <Gem size={12} color="#f5b738" />
                              <span>Upgrade to download</span>
                            </>
                          ) : (
                            "Download"
                          )}
                        </button>
                      </div>
                    )}

                  <div style={{ marginTop: "auto" }}>
                    {isFreeUser ? (
                      <>
                        <button 
                          onClick={() => navigate("/subscription")}
                          style={{
                            background: "#f5b738",
                            color: "#78350E",
                            border: "1px solid #e0a328",
                            boxShadow: "0 2px 8px rgba(245, 183, 56, 0.25)",
                            height: "44px",
                            borderRadius: "6px",
                            fontWeight: 500,
                            fontSize: "13px",
                            cursor: "pointer",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            transition: "all 0.15s ease"
                          }}
                        >
                          <Gem size={15} color="#78350E" className="stroke-[1.8]" />
                          <span>Upgrade to download report</span>
                        </button>
                        <div style={{ fontSize: "10px", color: "var(--color-text-subtle)", textAlign: "center", marginTop: "8px" }}>
                          Available with Business Pro &amp; Enterprise • Instant PDF download
                        </div>
                      </>
                    ) : (
                      <>
                        <button 
                          onClick={() => !activeReportJob && setIsReportModalOpen(true)} 
                          disabled={!!activeReportJob}
                          style={{ background: activeReportJob ? "var(--color-surface-2)" : "#6366F1", color: activeReportJob ? "var(--color-text-muted)" : "white", border: "none", height: "44px", borderRadius: "6px", fontWeight: "bold", fontSize: "13px", cursor: activeReportJob ? "not-allowed" : "pointer", width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", transition: "all 0.2s ease" }}
                        >
                           {activeReportJob ? (
                             <>
                               <RefreshCw size={14} className="animate-spin" />
                               Generating...
                             </>
                           ) : (
                             "Generate report"
                           )}
                        </button>
                        {!activeReportJob && <div style={{ fontSize: "10px", color: "var(--color-text-subtle)", textAlign: "center", marginTop: "8px" }}>Usually 1 to 2 minutes - PDF</div>}
                      </>
                    )}
                  </div>
                </div>
                </>
)}
              </div>
              <div style={{ ...staggerStyle(3), display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px" }}>
{(!revealReady || (revealReady && !scanFinal && !userForcedEscape)) ? (
  <>
    <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "10px 14px", minHeight: "80px" }}><Skeleton rows={2} /></div>
    <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "10px 14px", minHeight: "80px" }}><Skeleton rows={2} /></div>
    <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "10px 14px", minHeight: "80px" }}><Skeleton rows={2} /></div>
  </>
) : (
                <>
                <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderTop: "2px solid #e24b4a", borderRadius: "6px", padding: "10px 14px", display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ fontSize: "9px", fontWeight: 700, color: "var(--color-text-muted)", letterSpacing: "0.05em" }}>CRITICAL HITS</span>
                  <span style={{ fontSize: "18px", fontWeight: 700, color: "var(--color-text)" }}>{criticalCount}</span>
                  <span style={{ fontSize: "9px", color: "var(--color-text-subtle)" }}>Ransomware & direct victims</span>
                </div>
                <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderTop: "2px solid #ef9f27", borderRadius: "6px", padding: "10px 14px", display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ fontSize: "9px", fontWeight: 700, color: "var(--color-text-muted)", letterSpacing: "0.05em" }}>HIGH SEVERITY</span>
                  <span style={{ fontSize: "18px", fontWeight: 700, color: "var(--color-text)" }}>{highCount}</span>
                  <span style={{ fontSize: "9px", color: "var(--color-text-subtle)" }}>Finance, gov & credentials</span>
                </div>
                <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderTop: "2px solid #2d7dd2", borderRadius: "6px", padding: "10px 14px", display: "flex", flexDirection: "column", gap: "2px" }}>
                  <span style={{ fontSize: "9px", fontWeight: 700, color: "var(--color-text-muted)", letterSpacing: "0.05em" }}>TOTAL MATCHES</span>
                  <span style={{ fontSize: "18px", fontWeight: 700, color: "var(--color-text)" }}>{totalMatches}</span>
                  <span style={{ fontSize: "9px", color: "var(--color-text-subtle)" }}>Mentions across database</span>
                </div>
                </>
)}
              </div>

              {(!revealReady || (revealReady && !scanFinal && !userForcedEscape)) ? (
                <>
                  <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "10px 14px", height: "40px", marginBottom: "16px" }}><Skeleton rows={1} /></div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "14px", minHeight: "120px" }}><Skeleton rows={3} /></div>
                    <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "14px", minHeight: "120px" }}><Skeleton rows={3} /></div>
                  </div>
                </>
              ) : (
                <>
                  {banner && (
                    <div style={{ ...staggerStyle(4), background: banner.bg, border: `1px solid ${banner.border}`, color: banner.color, borderRadius: "6px", padding: "10px 14px", display: "flex", alignItems: "center", gap: "10px", fontSize: "12px", fontWeight: 600, marginBottom: "16px" }}>
                      {banner.icon}
                      <span style={{ flex: 1 }}>{banner.message}</span>
                    </div>
                  )}

                  <div style={{ ...staggerStyle(5) }}>
                    {filteredFindings.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                    {filteredFindings.map((leak: any) => {
                      const sevColor = getSeverityColor(leak.severity);
                      const sevLabel = getSeverityLabel(leak.severity);
                      
                      return (
                        <div key={leak._id} style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderLeft: `2px solid ${sevColor}`, borderRadius: "6px", padding: "14px", display: "flex", flexDirection: "column", gap: "10px" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: "6px", flex: 1, minWidth: 0 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                                <span style={{ background: `rgba(${sevColor === '#e24b4a' ? '226,75,74' : sevColor === '#ef9f27' ? '239,159,39' : '45,125,210'}, 0.12)`, color: sevColor, border: `1px solid rgba(${sevColor === '#e24b4a' ? '226,75,74' : sevColor === '#ef9f27' ? '239,159,39' : '45,125,210'}, 0.25)`, borderRadius: "4px", padding: "2px 6px", fontSize: "9px", fontWeight: 700 }}>
                                  {sevLabel}
                                </span>
                                <ForumTypeBadge type={leak.forum_type} />
                              </div>
                              <h3 style={{ fontSize: "14px", fontWeight: 700, color: "var(--color-text)", margin: 0, wordBreak: "break-word" }}>
                                {leak.thread_title_list || leak.title || "Untitled Leak"}
                              </h3>
                              <div style={{ fontSize: "11px", color: "var(--color-text-subtle)", display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                                <span>Forum: <strong style={{ color: "var(--color-text-muted)" }}>{leak.forum || "unknown"}</strong></span>
                                <span>Network: <strong style={{ color: "var(--color-text-muted)", textTransform: "uppercase" }}>{leak.network}</strong></span>
                                <span>Detected: <strong style={{ color: "var(--color-text-muted)" }}>{new Date(leak.created_at).toLocaleDateString()}</strong></span>
                              </div>
                              {(leak.match_fields || []).length > 0 && (
                                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "2px" }}>
                                  {leak.match_fields.map((mf: string, idx: number) => (
                                    <span key={idx} style={{ background: "var(--color-surface-2)", border: "1px solid var(--card-border)", color: "var(--color-text-muted)", fontSize: "9px", padding: "2px 6px", borderRadius: "12px", textTransform: "uppercase" }}>
                                      {mf}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                            <button 
                              onClick={() => handleWatchlistToggle(leak.domain || activeDomain)} 
                              style={{ background: "transparent", border: "1px solid var(--card-border)", color: "var(--color-text-subtle)", borderRadius: "4px", padding: "4px 8px", fontSize: "10px", cursor: "pointer", display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}
                            >
                              <BookmarkPlus size={12} /> Watch
                            </button>
                          </div>
                          {<FindingMatchViewer leak={leak} activeDomain={activeDomain} />}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "6px", padding: "40px 20px", textAlign: "center" }}>
                    <CheckCircle size={32} style={{ color: "#3fb68b", margin: "0 auto 12px auto" }} />
                    <h3 style={{ fontSize: "14px", fontWeight: 600, color: "var(--color-text)", margin: "0 0 8px 0" }}>No exposures found</h3>
                    <p style={{ fontSize: "12px", color: "var(--color-text-muted)", margin: 0, maxWidth: "400px", marginLeft: "auto", marginRight: "auto" }}>
                      No exposure found among {searchResult.posts_scanned || 0} posts as of {new Date().toLocaleDateString()}. This does not guarantee the domain is safe.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>


        <div className="right-rail">
          {activeJob && (
            <div style={{ background: "rgba(45, 125, 210, 0.05)", border: "1px solid rgba(45, 125, 210, 0.3)", borderRadius: "8px", padding: "14px", display: "flex", flexDirection: "column", gap: "8px" }}>
               <h4 style={{ fontSize: "11px", fontWeight: 700, color: "#2d7dd2", letterSpacing: "0.05em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px", margin: 0 }}>
                 <RefreshCw size={13} className="animate-spin" />
                 Report in progress
               </h4>
               <div style={{ fontSize: "11px", color: "var(--color-text)" }}>{activeJob.domain}</div>
               <div style={{ fontSize: "10px", color: "var(--color-text-muted)" }}>Elapsed: {elapsedTime}s</div>
            </div>
          )}

          <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "8px", padding: "14px" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
              <h4 style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-text)", letterSpacing: "0.05em", textTransform: "uppercase", margin: 0, display: "flex", alignItems: "center", gap: "6px" }}>
                <BookmarkPlus size={13} style={{ color: "#3fb68b" }} />
                Watchlist
              </h4>
              <button
                onClick={() => setIsAddDomainModalOpen(true)}
                style={{
                  background: "rgba(63, 182, 139, 0.12)",
                  border: "1px solid rgba(63, 182, 139, 0.3)",
                  color: "#3fb68b",
                  borderRadius: "4px",
                  padding: "3px 8px",
                  fontSize: "11px",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                }}
                title="Add domain for continuous monitoring or watchlist"
              >
                <Plus size={12} />
                <span>Add Domain</span>
              </button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              {(() => {
                const clientProfileEntries = watchlist.filter(w => w.source === "client_profile" || w.source === "continuous");
                const manualWatchlistEntries = watchlist.filter(w => w.source !== "client_profile" && w.source !== "continuous");

                return (
                  <>
                    {/* Continuous Monitoring Group */}
                    <div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                        <div style={{ fontSize: "9px", fontWeight: 700, color: "#2dd260", textTransform: "uppercase", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: "5px" }}>
                          <span style={{ width: "5px", height: "5px", borderRadius: "50%", background: "#2dd260" }} />
                          Continuous Monitoring ({clientProfileEntries.length})
                        </div>
                      </div>
                      {clientProfileEntries.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", maxHeight: "240px", overflowY: "auto", paddingRight: "4px" }}>
                          {clientProfileEntries.map((item) => (
                            <div key={item.domain} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--card-border)" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px", overflow: "hidden" }}>
                                <span style={{ fontSize: "9px", background: "rgba(45, 210, 96, 0.15)", color: "#2dd260", padding: "1px 4px", borderRadius: "3px", fontWeight: 700 }}>LIVE</span>
                                <button
                                  onClick={() => { setDomainInput(item.domain); loadDomain(item.domain, { source: 'watchlist' }); }}
                                  style={{ background: "none", border: "none", padding: 0, textAlign: "left", fontSize: "11px", color: "var(--color-text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "105px", cursor: "pointer", fontFamily: "monospace" }}
                                  title={`${item.label ? item.label + ' - ' : ''}${item.domain}`}
                                >
                                  {item.domain}
                                </button>
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <span style={getWatchlistBadgeStyle(item.last_match_count || 0)}>{item.last_match_count || 0}</span>
                                <button
                                  onClick={async () => {
                                    await domainStore.removeDomain(item.domain);
                                    toast.success(`Removed ${item.domain} from continuous monitoring`);
                                  }}
                                  style={{ background: "none", border: "none", color: "var(--color-text-subtle)", cursor: "pointer", padding: "2px" }}
                                  title={`Remove ${item.domain}`}
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div style={{ fontSize: "10.5px", color: "var(--color-text-subtle)", padding: "8px", background: "var(--color-surface-2)", borderRadius: "4px", border: "1px dashed var(--card-border)", lineHeight: 1.4 }}>
                          No continuous monitoring assets configured. Click <strong>+ Add Domain</strong> to enable live 24/7 dark web alerts.
                        </div>
                      )}
                    </div>

                    {/* Manual Watchlist Group */}
                    <div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "6px" }}>
                        <div style={{ fontSize: "9px", fontWeight: 700, color: "var(--color-text-subtle)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          Manual Watchlist ({manualWatchlistEntries.length})
                        </div>
                      </div>
                      {manualWatchlistEntries.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", maxHeight: "200px", overflowY: "auto", paddingRight: "4px" }}>
                          {manualWatchlistEntries.map(item => (
                            <div key={item.domain} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--card-border)" }}>
                              <button
                                onClick={() => { setDomainInput(item.domain); loadDomain(item.domain, { source: 'watchlist' }); }}
                                style={{ background: "none", border: "none", color: "#3fb68b", fontSize: "11px", cursor: "pointer", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "120px", textAlign: "left" }}
                                title={item.domain}
                              >
                                {item.domain}
                              </button>
                              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                                <span style={getWatchlistBadgeStyle(item.last_match_count || 0)}>{item.last_match_count || 0}</span>
                                <button
                                  onClick={async () => {
                                    await domainStore.removeDomain(item.domain);
                                    toast.success(`Removed ${item.domain} from watchlist`);
                                  }}
                                  style={{ background: "none", border: "none", color: "var(--color-text-subtle)", cursor: "pointer", padding: "2px" }}
                                  title={`Remove ${item.domain}`}
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span style={{ fontSize: "11px", color: "var(--color-text-subtle)" }}>No manual entries</span>
                      )}
                    </div>

                    {/* Quick Add Active Domain Shortcut */}
                    {activeDomain && !isWatched && (
                      <button
                        onClick={() => {
                          setIsAddDomainModalOpen(true);
                        }}
                        style={{
                          marginTop: "2px",
                          padding: "7px 10px",
                          borderRadius: "6px",
                          fontSize: "11px",
                          fontWeight: 600,
                          background: "rgba(45, 210, 96, 0.08)",
                          border: "1px dashed rgba(45, 210, 96, 0.35)",
                          color: "#2dd260",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          cursor: "pointer",
                          width: "100%",
                          transition: "all 0.15s ease",
                        }}
                      >
                        <Plus size={12} />
                        <span>Add "{activeDomain}" to Monitoring</span>
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          <div style={{ background: "var(--card-bg)", border: "1px solid var(--card-border)", borderRadius: "8px", padding: "14px" }}>
            <h4 style={{ fontSize: "11px", fontWeight: 700, color: "var(--color-text)", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
              <History size={13} style={{ color: "#3fb68b" }} />
              Recent searches
            </h4>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {history.length > 0 ? (
                history.slice(0, 4).map((item) => (

                  <div key={item._id} className="recent-search-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "11px" }}>
                    <button onClick={() => { setDomainInput(item.domain); loadDomain(item.domain, { source: 'recent' }); }} style={{ background: "none", border: "none", padding: 0, fontFamily: "monospace", color: "var(--color-text-muted)", cursor: "pointer", textAlign: "left" }}>
                      {item.domain}
                    </button>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ color: "var(--color-text-subtle)", fontSize: "9px" }}>
                        {item.status === 'identifying' ? 'Identifying...' : `${item.hits !== undefined ? item.hits : '-'} hit${item.hits === 1 ? '' : 's'} · `}
                        {item.status !== 'identifying' && (
                          <span title={new Date(item.last_searched_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).replace(',', '')}>
                            {(() => {
                              const ms = Date.now() - new Date(item.last_searched_at).getTime();
                              const mins = Math.floor(ms / 60000);
                              if (mins < 1) return "Just now";
                              if (mins < 60) return `${mins} min ago`;
                              if (mins < 1440) return `${Math.floor(mins/60)} hr ago`;
                              return `${Math.floor(mins/1440)}d ago`;
                            })()}
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <span style={{ fontSize: "11px", color: "var(--color-text-subtle)" }}>No recent searches</span>
              )}
            </div>
          </div>

          <div style={{ background: "rgba(63, 182, 139, 0.05)", border: "1px solid rgba(63, 182, 139, 0.2)", borderRadius: "6px", padding: "12px" }}>
            <h4 style={{ fontSize: "11px", fontWeight: 700, color: "#3fb68b", marginBottom: "6px", display: "flex", alignItems: "center", gap: "6px" }}>
              <Info size={12} />
              Scan coverage
            </h4>
            <div style={{ position: "relative" }}>
               <p style={{ fontSize: "10px", color: "var(--color-text-muted)", margin: 0, lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                 Queries database for domain matches across fields: direct website victim fields, text body, HTML code, source thread URI x, direct attachment links, Tor .onion nodes, and thread title lists.
               </p>
               <button onClick={(e) => { e.currentTarget.previousElementSibling!.setAttribute("style", "font-size: 10px; color: var(--color-text-muted); margin: 0; line-height: 1.4;"); e.currentTarget.style.display = 'none'; }} style={{ background: "none", border: "none", color: "#3fb68b", fontSize: "10px", padding: 0, cursor: "pointer", textDecoration: "underline", marginTop: "2px" }}>More</button>
            </div>
          </div>
        </div>
      </div>
      <ReportModal 
        isOpen={isReportModalOpen} 
        onClose={() => setIsReportModalOpen(false)}
        domain={activeDomain}
        companyName={profileState.profile?.company_name || ""}
        sector={profileState.profile?.sector || ""}
        sectorConfidence={profileState.profile?.confidence > 0.5 ? "medium" : "low"}
        sectorConfirmed={false}
        existingReports={domainReports}
        userRole={user?.role || "client"}
        onStartJob={(job) => {
          setActiveReportJob(job);
          setActiveJob(job);
        }}
        onConflict={(job, msg) => { 
          setActiveReportJob(job); 
          setActiveJob(job);
          toast.info(typeof msg === 'string' ? msg : (msg?.message || "A report is already being generated.")); 
        }}
        onDownloadExisting={async (id) => {
           try {
             const blob = await downloadReportPdf(id);
             const url = window.URL.createObjectURL(blob);
             const a = document.createElement("a");
             a.href = url;
             a.download = `Report_${activeDomain}.pdf`;
             document.body.appendChild(a);
             a.click();
             window.URL.revokeObjectURL(url);
           } catch(e) {
             toast.error("Download failed");
           }
        }}
      />
      <AddDomainModal
        isOpen={isAddDomainModalOpen}
        onClose={() => setIsAddDomainModalOpen(false)}
        defaultDomain={activeDomain || ""}
        defaultSource="client_profile"
        onSuccess={(domain) => {
          setDomainInput(domain);
          loadDomain(domain, { source: "watchlist" });
        }}
      />
    </div>
  );

}
